window.BundleBuilder = (() => {
  window.__ttBundleBuilderSuppressCartDrawer = false;

  document.addEventListener(
    'variant:add',
    (event) => {
      if (window.__ttBundleBuilderSuppressCartDrawer && event.detail) {
        event.detail.blockCartDrawerOpening = true;
      }
    },
    true
  );

  const rows = new Map();
  let latestCart = null;
  const pendingQuantities = new Map();
  const activeRequests = new Map();

  function clampMin(value, min) {
    return Math.max(min, value);
  }

  function isTrue(value) {
    return value === true || value === 'true' || value === '1';
  }

  function getRowState(row) {
    const card = row.closest('.horizontal-product');
    const bundleRoot = row.closest('[data-bundle-builder]');
    const input = row.querySelector('[data-qty-input]');
    const decreaseButton = row.querySelector('[data-qty-decrease]');
    const increaseButton = row.querySelector('[data-qty-increase]');
    const quickBuy = card?.querySelector('[data-quick-buy-cta]');
    const quickAddToggle = card?.querySelector('[data-quick-add-toggle]');
    const checkoutButton = bundleRoot?.querySelector('button[name="checkout"]');
    const variantId = Number(row.dataset.variantId);

    const excludeFromBundle = isTrue(row.dataset.excludeFromBundle);

    const moneyFormat =
      bundleRoot?.dataset.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat ||
      window.Shopify?.money_format ||
      '${{amount}}';

    return {
      row,
      card,
      bundleRoot,
      input,
      decreaseButton,
      increaseButton,
      quickBuy,
      quickAddToggle,
      checkoutButton,
      variantId,
      excludeFromBundle,
      moneyFormat
    };
  }

  function formatMoney(cents, moneyFormat) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const amount = (cents / 100).toFixed(2);

    return moneyFormat.replace(
      /\{\{\s*amount(?:_with_comma_separator|_no_decimals|_no_decimals_with_comma_separator)?\s*\}\}/,
      amount
    );
  }

  function getCartQuantity(variantId) {
    if (!latestCart || !window.TTCart) return 0;
    return window.TTCart.getQuantityForVariant(latestCart, variantId);
  }

  function getBundleQuantity() {
    return getBundleQuantityFromCart(latestCart);
  }

  function getBundleQuantityFromCart(cart) {
    if (!cart || !window.TTCart) return 0;

    let total = 0;

    rows.forEach((state) => {
      if (state.excludeFromBundle) return;
      total += window.TTCart.getQuantityForVariant(cart, state.variantId);
    });

    return total;
  }

  function getBundleVariantIds() {
    const ids = [];

    rows.forEach((state) => {
      if (state.excludeFromBundle) return;
      ids.push(state.variantId);
    });

    return ids;
  }

  function updateBundleTotalsForRoot(bundleRoot, cart) {
    if (!bundleRoot || !cart) return;

    const totalEl = bundleRoot.querySelector('[id^="bundle-total-"]');
    const originalEl = bundleRoot.querySelector('[id^="bundle-original-"]');

    if (!totalEl) return;

    const moneyFormat =
      bundleRoot.dataset.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat ||
      window.Shopify?.money_format ||
      '${{amount}}';

    const cartDrawerTotal = cart.total_price || 0;

    const originalSubtotal = (cart.items || []).reduce((sum, item) => {
      if (window.TTCart?.isSellingPlanItem?.(item)) return sum;
      return sum + (item.original_line_price || item.price * item.quantity);
    }, 0);

    totalEl.innerHTML = formatMoney(cartDrawerTotal, moneyFormat);

    if (originalEl) {
      if (originalSubtotal > cartDrawerTotal) {
        originalEl.innerHTML = formatMoney(originalSubtotal, moneyFormat);
        originalEl.hidden = false;
      } else {
        originalEl.innerHTML = '';
        originalEl.hidden = true;
      }
    }
  }

  function dispatchBundleQuantityChange() {
    document.dispatchEvent(
      new CustomEvent('bundle:quantity-change', {
        bubbles: true,
        detail: {
          quantity: getBundleQuantity(),
          cart: latestCart
        }
      })
    );
  }

  function renderAll(cart = null) {
    latestCart = cart || latestCart;
    if (!latestCart) return;

    rows.forEach((state) => {
      const cartQuantity = getCartQuantity(state.variantId);
      const hasPending = pendingQuantities.has(state.variantId);

      const displayedQuantity = hasPending
        ? pendingQuantities.get(state.variantId)
        : cartQuantity;

      state.input.value = displayedQuantity;

      if (state.quickBuy) {
        const shouldShowQuickBuy = displayedQuantity < 1 && !hasPending;

        state.row.hidden = shouldShowQuickBuy;
        state.quickBuy.hidden = !shouldShowQuickBuy;
      } else {
        state.row.hidden = false;
      }

      if (state.card) {
        state.card.classList.toggle('is-selected', displayedQuantity > 0);
        state.card.classList.toggle('is-updating', hasPending);
        state.card.classList.toggle(
          'is-excluded-from-bundle',
          state.excludeFromBundle
        );
      }

      if (state.checkoutButton) {
        state.checkoutButton.disabled = pendingQuantities.size > 0;
      }
    });

    const updatedRoots = new Set();

    rows.forEach((state) => {
      if (state.bundleRoot && !updatedRoots.has(state.bundleRoot)) {
        updatedRoots.add(state.bundleRoot);
        updateBundleTotalsForRoot(state.bundleRoot, latestCart);
      }
    });

    dispatchBundleQuantityChange();

    if (window.TTCartProgress) {
      window.TTCartProgress.update(latestCart);
    }
  }

  async function changeVariantQuantity(variantId, nextQty) {
    const safeQty = clampMin(nextQty, 0);

    pendingQuantities.set(variantId, safeQty);
    renderAll();

    if (activeRequests.has(variantId)) return;

    activeRequests.set(variantId, true);
    window.__ttBundleBuilderSuppressCartDrawer = true;

    try {
      while (pendingQuantities.has(variantId)) {
        const qtyToApply = pendingQuantities.get(variantId);

        const updatedCart = await window.TTCart.setVariantQuantity(
          variantId,
          qtyToApply
        );

        latestCart = updatedCart;

        const cartQty = getCartQuantity(variantId);

        if (pendingQuantities.get(variantId) === cartQty) {
          pendingQuantities.delete(variantId);
        }

        renderAll(latestCart);
      }
    } catch (error) {
      console.error(error);
      pendingQuantities.delete(variantId);

      try {
        const cart = await window.TTCart.fetchCart();
        renderAll(cart);
      } catch (fetchError) {
        console.error(fetchError);
        renderAll();
      }
    } finally {
      activeRequests.delete(variantId);

      setTimeout(() => {
        window.__ttBundleBuilderSuppressCartDrawer = false;
      }, 250);
    }
  }

  function initQuantityRow(row) {
    if (!row || row.__ttQuantityRowInitialized) return;

    row.__ttQuantityRowInitialized = true;

    const state = getRowState(row);

    if (!state.input || !state.variantId || !window.TTCart) return;

    rows.set(row, state);

    if (state.quickAddToggle) {
      state.quickAddToggle.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        state.quickAddToggle.setAttribute('aria-busy', 'true');

        const currentQty = getCartQuantity(state.variantId);

        try {
          await changeVariantQuantity(state.variantId, currentQty + 1);
        } finally {
          state.quickAddToggle.removeAttribute('aria-busy');
        }
      });
    }

    state.input.addEventListener('input', () => {
      state.input.value = state.input.value.replace(/[^\d]/g, '');
    });

    state.input.addEventListener('change', () => {
      const targetQty = clampMin(
        parseInt(state.input.value || '0', 10) || 0,
        0
      );

      changeVariantQuantity(state.variantId, targetQty);
    });

    if (state.decreaseButton) {
      state.decreaseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        requestAnimationFrame(() => {
          state.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    if (state.increaseButton) {
      state.increaseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        requestAnimationFrame(() => {
          state.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  function reconcilePending(cart) {
    rows.forEach((state) => {
      const cartQty = window.TTCart.getQuantityForVariant(
        cart,
        state.variantId
      );

      if (pendingQuantities.has(state.variantId)) {
        const pendingQty = pendingQuantities.get(state.variantId);

        if (pendingQty === cartQty) {
          pendingQuantities.delete(state.variantId);
        }
      }
    });
  }

  async function sync() {
    if (!window.TTCart) return;

    try {
      const cart = await window.TTCart.fetchCart();
      latestCart = cart;
      reconcilePending(latestCart);
      renderAll(latestCart);
    } catch (error) {
      console.error(error);
    }
  }

  function autoInit() {
    document
      .querySelectorAll('[data-cart-quantity-row]')
      .forEach(initQuantityRow);

    sync();
  }

  document.addEventListener('DOMContentLoaded', autoInit);
  document.addEventListener('shopify:section:load', autoInit);

  document.addEventListener('cart:change', (event) => {
    if (event?.detail?.cart) {
      latestCart = event.detail.cart;
      reconcilePending(latestCart);
      renderAll(latestCart);
    } else {
      sync();
    }
  });

  return {
    init: autoInit,
    sync,
    getBundleQuantity,
    getBundleQuantityFromCart,
    getBundleVariantIds
  };
})();